/**
 * Plain printable page, like twin-ci's "Printable View" pages (e.g. accounting_print).
 *
 * Opens a new tab holding only a heading and a plain HTML table of rows the page already has -
 * no app layout or styling to fight with, so the table also copies cleanly into Excel - and opens
 * the print dialog straight away. Call it from a click handler (browsers block pop-ups otherwise).
 */
export interface PrintableTable {
  title: string;          // browser tab title
  heading: string;        // first line on the page (plain text)
  columns: string[];
  rows: any[][];
  numeric?: number[];     // column indexes to right-align
}

export function openPrintable(t: PrintableTable): boolean {
  const right = (i: number) => (t.numeric || []).indexOf(i) !== -1 ? ' style="text-align:right"' : '';
  return openPrintWindow(t.title,
    'h4{font-size:15px;margin:0 0 12px}table{border-collapse:collapse;width:100%}'
    + 'th,td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top}th{background:#eee}'
    + '@media print{body{margin:0}tr{page-break-inside:avoid}}',
    '<h4>' + escapeHtml(t.heading) + '</h4>'
    + (t.rows.length
      ? '<table><thead><tr>' + t.columns.map((c, i) => '<th' + right(i) + '>' + escapeHtml(c) + '</th>').join('') + '</tr></thead><tbody>'
        + t.rows.map(r => '<tr>' + r.map((v, i) => '<td' + right(i) + '>' + escapeHtml(v) + '</td>').join('') + '</tr>').join('')
        + '</tbody></table>'
      : '<p>No Results</p>'));
}

/**
 * Lower level: a new tab with the given body HTML (build it with escapeHtml) and page CSS, then
 * the print dialog. For print pages that are not one plain table.
 */
export function openPrintWindow(title: string, css: string, body: string): boolean {
  const win = window.open('', '_blank');
  if (!win) {
    return false;   // pop-up blocked
  }
  win.document.open();
  win.document.write('<!doctype html><html><head><meta charset="utf-8"><title>' + escapeHtml(title) + '</title>'
    + '<style>body{font-family:Arial,Helvetica,sans-serif;font-size:12px;margin:24px;color:#000}' + css + '</style></head><body>'
    + body + '</body></html>');
  win.document.close();
  win.focus();
  win.print();
  return true;
}

export function escapeHtml(v: any): string {
  return (v === null || v === undefined ? '' : String(v))
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** 1234.5 -> "1,234.50"; '' / null stay empty. */
export function plainAmount(value: any): string {
  if (value === '' || value === null || value === undefined || isNaN(+value)) {
    return '';
  }
  return (+value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
